"use client"

import React, { useReducer, ReactNode, useCallback } from "react"

import { useAuthToken } from "@lib/hooks/use-auth-token"
import { CheckoutExperimentsDefinition } from "@modules/signup-and-order-flow/experiments/experiment-interfaces"

import { useIdentityVerificationStatus, useLoanApplicationStatus } from "./session-queries"
import { sessionReducer, createInitialState } from "./session-reducer"
import { SessionActionType, FlowType } from "./session-types"
import { SessionContext } from "./use-session"

interface SessionProviderProps {
  children: ReactNode
  treatments: CheckoutExperimentsDefinition
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children, treatments }) => {
  const authToken = useAuthToken()

  const { data: idvResponse, isLoading: idvLoading, refetch: refetchIdv } = useIdentityVerificationStatus(authToken)

  const { data: loanApplicationResponse, refetch: refetchLoanApplication } = useLoanApplicationStatus(authToken)

  const [sessionData, dispatch] = useReducer(sessionReducer, treatments, createInitialState)

  // Convenient methods
  const updateProduct = useCallback((updates: Partial<typeof sessionData.product>) => {
    dispatch({ type: SessionActionType.UPDATE_PRODUCT, payload: updates })
  }, [])

  const updateUser = useCallback((updates: Partial<typeof sessionData.user>) => {
    dispatch({ type: SessionActionType.UPDATE_USER, payload: updates })
  }, [])

  const updateProgress = useCallback((updates: Partial<typeof sessionData.progress>) => {
    dispatch({ type: SessionActionType.UPDATE_PROGRESS, payload: updates })
  }, [])

  const updateFlowType = useCallback((flowType: FlowType) => {
    dispatch({ type: SessionActionType.UPDATE_FLOW_TYPE, payload: flowType })
  }, [])

  const resetSession = useCallback(() => {
    dispatch({ type: SessionActionType.RESET_SESSION })
  }, [])

  return (
    <SessionContext.Provider
      value={{
        sessionData,
        dispatch,
        isLoggedIn: !!authToken,
        updateProduct,
        updateUser,
        updateProgress,
        updateFlowType,
        resetSession,
        refetchIdv,
        idvLoading,
        idvStatus: idvResponse ?? null,
        loanApplicationStatus: loanApplicationResponse ?? null,
        refetchLoanApplication,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
